<template>
  <div class="page-root pet-select">
    <h1><span class="font-weight-bold">펫 선택</span></h1>

    <!-- 무대(스포트라이트) -->
    <div class="stage">
      <!-- 좌 -->
      <button class="nav-btn nav-left" :disabled="selectedIndex === 0" @click="prev" aria-label="이전">
        <v-icon size="36">mdi-chevron-left</v-icon>
      </button>

      <div class="viewport">
        <div class="track" :style="trackStyle">
          <div v-for="(slot, i) in slots" :key="i" class="slot" :class="{ active: i === selectedIndex }">
            <!-- 스포트라이트 (활성 슬롯 뒤) -->
            <div v-if="i === selectedIndex" class="spotlight"></div>

            <!-- 펫 슬롯 -->
            <div v-if="!slot.empty" class="pet-card">
              <img class="pet-thumb" :src="thumbUrl(slot.modelIndex)" :alt="slot.modelIndex" @error="onThumbError" />
            </div>
            <!-- 빈(추가) 슬롯 -->
            <div v-else class="pet-card add-card">
              <v-icon size="64" color="#b39ddb">mdi-plus</v-icon>
            </div>
          </div>
        </div>
      </div>

      <!-- 우 -->
      <button class="nav-btn nav-right" :disabled="selectedIndex === slots.length - 1" @click="next" aria-label="다음">
        <v-icon size="36">mdi-chevron-right</v-icon>
      </button>
    </div>

    <!-- 이름 -->
    <div class="pet-name">{{ currentName }}</div>
    <div class="pet-sub">{{ currentSub }}</div>

    <!-- 확인 -->
    <v-btn color="primary" large rounded class="confirm-btn" @click="onConfirm">
      {{ current.empty ? "펫 추가하기" : "이 펫으로 변경" }}
    </v-btn>
  </div>
</template>

<script>
import SupabaseApi from "@/api/SupabaseApi";

const CARD_W = 180; // 카드 폭(px)
const GAP = 40; // 카드 간격(px)

export default {
  name: "PetSelectPage",
  data() {
    return {
      pets: [],
      selectedIndex: 0,
    };
  },
  async created() {
    try {
      this.pets = (await SupabaseApi.PetProfile.fetchAllPets()) || [];
      // 현재 선택 펫으로 초기 하이라이트 (없으면 첫 펫=0)
      const selectedPetId = await SupabaseApi.UserStatus.fetchSelectedPetId();
      if (selectedPetId) {
        const idx = this.pets.findIndex((p) => p.id === selectedPetId);
        if (idx >= 0) this.selectedIndex = idx;
      }
    } catch (e) {
      console.error(e);
    }
  },
  computed: {
    // 슬롯 = 내 펫들 + 빈 슬롯 1개(+1)
    slots() {
      const petSlots = this.pets.map((p, idx) => ({
        empty: false,
        id: p.id,
        name: p.name || `펫 ${idx + 1}`,
        modelIndex: this.modelIndexFromUrl(p.modelUrl),
      }));
      return [...petSlots, { empty: true }];
    },
    current() {
      return this.slots[this.selectedIndex] || {};
    },
    currentName() {
      return this.current.empty ? "새 펫" : this.current.name;
    },
    currentSub() {
      if (this.current.empty) return "새로운 친구를 맞이하세요";
      return this.current.modelIndex ? this.current.modelIndex.toUpperCase() : "";
    },
    trackStyle() {
      // 선택된 카드 중심을 뷰포트 중앙(50%)에 위치
      const offset = this.selectedIndex * (CARD_W + GAP) + CARD_W / 2;
      return { transform: `translateX(calc(50% - ${offset}px))` };
    },
  },
  methods: {
    prev() {
      if (this.selectedIndex > 0) this.selectedIndex--;
    },
    next() {
      if (this.selectedIndex < this.slots.length - 1) this.selectedIndex++;
    },
    thumbUrl(modelIndex) {
      return `/trial/thumbnails/${modelIndex}_thumbnail.png`;
    },
    onThumbError(e) {
      e.target.src = "/trial/thumbnails/bear_thumbnail.png"; // 폴백
    },
    // model_url(예: models/v5/fox_v5+240415.glb) → modelIndex(fox)
    modelIndexFromUrl(url) {
      if (!url) return "bear";
      let base = url.split("/").pop().split("+")[0];
      base = base.replace(/\.glb$/i, "").replace(/_v\d+$/i, "");
      if (base.includes("-")) base = base.split("-").pop();
      return base.toLowerCase();
    },
    async onConfirm() {
      if (this.current.empty) {
        this.$router.push("/pet/new"); // 펫 추가 = 펫 생성 화면(추가 모드)
        return;
      }
      try {
        await SupabaseApi.UserStatus.setSelectedPet(this.current.id);
        // 사이드바 등 활성 펫 의존 UI 갱신: 선택 펫 id + 이름 즉시 반영 + 재로드 신호
        this.$store.commit("__setSelectedPetId", this.current.id);
        this.$store.commit("__setVirtupetName", this.current.name);
        this.$store.commit("__bumpPetRevision");
        this.notify(`"${this.currentName}" 펫으로 변경했어요.`);
      } catch (e) {
        console.error(e);
        this.notify("펫 변경에 실패했습니다.");
      }
    },
    notify(title) {
      this.$notify ? this.$notify({ type: "success", title, duration: 2000 }) : alert(title);
    },
  },
};
</script>

<style scoped>
.pet-select {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* 무대: 어두운 배경 + 스포트라이트 */
.stage {
  position: relative;
  width: 100%;
  max-width: 760px;
  height: 380px;
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  background: radial-gradient(ellipse at 50% 120%, #2a2540 0%, #15121f 70%);
  overflow: hidden;
}

.viewport {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.track {
  display: flex;
  gap: 40px;
  align-items: center;
  transition: transform 0.42s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;
}

.slot {
  position: relative;
  flex: 0 0 180px;
  height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: scale(0.78);
  opacity: 0.4;
  transition: transform 0.42s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.42s;
}
.slot.active {
  transform: scale(1.12);
  opacity: 1;
}

/* 조명 빔(위에서 내려오는 원뿔) */
.spotlight {
  position: absolute;
  top: -50px;
  left: 50%;
  transform: translateX(-50%);
  width: 220px;
  height: 360px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.45), rgba(255, 255, 255, 0));
  clip-path: polygon(38% 0, 62% 0, 100% 100%, 0 100%);
  filter: blur(6px);
  z-index: 0;
  pointer-events: none;
}

.pet-card {
  position: relative;
  z-index: 1;
  width: 180px;
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pet-thumb {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.slot.active .pet-thumb {
  filter: drop-shadow(0 0 22px rgba(255, 255, 255, 0.65));
}

.add-card {
  border: 2px dashed #6a5b8c;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.03);
}

/* 좌우 버튼 */
.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 5;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
}
.nav-btn:hover {
  background: #fff;
}
.nav-btn:active {
  transform: translateY(-50%) scale(0.92);
}
.nav-btn:disabled {
  opacity: 0.3;
  cursor: default;
}
.nav-left {
  left: 16px;
}
.nav-right {
  right: 16px;
}

.pet-name {
  margin-top: 24px;
  font-size: 28px;
  font-weight: 800;
  color: #2a2540;
}
.pet-sub {
  font-size: 13px;
  color: #9b8fb5;
  letter-spacing: 1px;
}

.confirm-btn {
  margin-top: 24px;
  min-width: 200px;
}
</style>
